import { useCallback, useEffect, useRef } from 'react';
import { useTripleClick } from './useTripleClick';
import { useTripleTouch } from './useTripleTouch';

const COOLDOWN_DURATION = 2000;

export function usePinTrigger({
  onTrigger,
  excludeSelectors = [],
  clickDelay = 500,
} = {}) {
  const isTriggered = useRef(false);
  const triggerCooldown = useRef(0);
  const resetTimerRef = useRef(null);
  const isTouchDevice = useRef(false);

  const safeTrigger = useCallback(() => {
    const now = Date.now();
    if (now - triggerCooldown.current < COOLDOWN_DURATION) return;
    if (isTriggered.current) return;

    isTriggered.current = true;
    triggerCooldown.current = now;
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => {
      isTriggered.current = false;
    }, 1000);
    onTrigger?.();
  }, [onTrigger]);

  useEffect(
    () => () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    },
    []
  );

  const { handleClick, reset: resetClick } = useTripleClick({
    delay: clickDelay,
    onTripleClick: safeTrigger,
  });

  const {
    handleTouchStart,
    handleTouchEnd,
    handleTouchMove,
    reset: resetTouch,
  } = useTripleTouch({
    onTripleTouch: safeTrigger,
    excludeSelectors,
  });

  useEffect(() => {
    isTouchDevice.current = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }, []);

  useEffect(() => {
    const handleGlobalClick = (event) => {
      if (isTouchDevice.current) return;
      const target = event.target;
      if (
        excludeSelectors.some(
          (selector) => target?.matches?.(selector) || target?.closest?.(selector)
        )
      ) {
        return;
      }
      handleClick(event);
    };

    const handleGlobalTouchStart = (event) => {
      if (event.touches?.length > 0 && isTouchDevice.current) handleTouchStart(event);
    };
    const handleGlobalTouchEnd = (event) => {
      if (isTouchDevice.current) handleTouchEnd(event);
    };
    const handleGlobalTouchMove = (event) => {
      if (isTouchDevice.current) handleTouchMove(event);
    };

    const touchOptions = { capture: true, passive: true };
    document.addEventListener('click', handleGlobalClick, true);
    document.addEventListener('touchstart', handleGlobalTouchStart, touchOptions);
    document.addEventListener('touchend', handleGlobalTouchEnd, touchOptions);
    document.addEventListener('touchcancel', handleGlobalTouchEnd, touchOptions);
    document.addEventListener('touchmove', handleGlobalTouchMove, touchOptions);

    return () => {
      document.removeEventListener('click', handleGlobalClick, true);
      document.removeEventListener('touchstart', handleGlobalTouchStart, true);
      document.removeEventListener('touchend', handleGlobalTouchEnd, true);
      document.removeEventListener('touchcancel', handleGlobalTouchEnd, true);
      document.removeEventListener('touchmove', handleGlobalTouchMove, true);
      resetClick();
      resetTouch();
    };
  }, [
    excludeSelectors,
    handleClick,
    handleTouchEnd,
    handleTouchMove,
    handleTouchStart,
    resetClick,
    resetTouch,
  ]);
}
